import { useState, useEffect } from 'react'
import { apiClient } from '@nyos/shared'
import { useYard } from '../contexts/YardContext'

export interface Scenario {
  name: string
  displayName: string
  usesNatsStreaming: boolean
  workflowType: string
  workflowDisplayName: string
}

export interface ScenarioControlsProps {
  workflowType: 'SIMULATION' | 'VALIDATION' | 'SWITCH_LIST'
  onScenarioChange?: (scenario: Scenario | null) => void
  onSetupComplete?: () => void
  onCleanupComplete?: () => void
  className?: string
}

interface SwitchScenarioResponse {
  status: string
  message: string
  scenarioType: string
  facilityId: string
}

interface SetupCleanupResponse {
  status: string
  message: string
}

const ScenarioControls: React.FC<ScenarioControlsProps> = ({
  workflowType,
  onScenarioChange,
  onSetupComplete,
  onCleanupComplete,
  className = ''
}) => {
  const { switchFacility } = useYard()
  const [scenarios, setScenarios] = useState<Scenario[]>([])
  const [selectedScenario, setSelectedScenario] = useState<Scenario | null>(null)
  const [activeValidationScenario, setActiveValidationScenario] = useState<Scenario | null>(null)
  const [loading, setLoading] = useState(true)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [setupLoading, setSetupLoading] = useState(false)
  const [cleanupLoading, setCleanupLoading] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [demoMode, setDemoMode] = useState(false)

  // Check demo mode from environment variable and backend config
  useEffect(() => {
    const checkDemoMode = async () => {
      const envDemoMode = import.meta.env.VITE_DEMO_MODE === 'true'
      
      try {
        const config = await apiClient.get<{ enabled: boolean }>('/api/config/demo')
        setDemoMode(envDemoMode && config.enabled)
      } catch (error) {
        // Fallback to environment variable if backend call fails
        setDemoMode(envDemoMode)
      }
    }
    
    checkDemoMode()
  }, [])

  // Escape closes the scenario dropdown (matches ScopeSwitcher dismissal).
  useEffect(() => {
    if (!dropdownOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDropdownOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [dropdownOpen])

  // Filter scenarios based on workflow type
  const filteredScenarios = scenarios.filter(scenario => scenario.workflowType === workflowType)

  // Simulation workflow: traditional automatic switching
  const switchScenario = async (scenarioType: string) => {
    if (selectedScenario?.name === scenarioType || scenarioType === '') {
      return
    }

    try {
      const response = await apiClient.post<SwitchScenarioResponse>('/api/simulation/switch', { scenarioType })

      // Update context with the facilityId from response to trigger YardMap re-render
      if (response.facilityId) {
        switchFacility(response.facilityId)
      }

      const scenario = scenarios.find(s => s.name === scenarioType)
      setSelectedScenario(scenario || null)
      onScenarioChange?.(scenario || null)
    } catch (error) {
      console.error('Failed to switch scenario:', error)
    }
  }

  // Stop simulation and clear yard
  const stopSimulation = async () => {
    try {
      await apiClient.post('/api/simulation/stop', {})
      setSelectedScenario(null)
      onScenarioChange?.(null)
      // Clear yard context to show blank state
      switchFacility('')
    } catch (error) {
      console.error('Failed to stop simulation:', error)
    }
  }

  // Validation workflow: manual setup
  const setupValidationScenario = async () => {
    if (!selectedScenario) return

    setSetupLoading(true)
    setActionError(null)
    try {
      await apiClient.post<SetupCleanupResponse>(`/api/scenarios/${selectedScenario.name}/setup`, {})
      setActiveValidationScenario(selectedScenario)
      onScenarioChange?.(selectedScenario)
      onSetupComplete?.()
    } catch (error) {
      console.error('Failed to setup validation scenario:', error)
      setActionError('Setup failed — see backend logs.')
    } finally {
      setSetupLoading(false)
    }
  }

  // Validation workflow: manual cleanup
  const cleanupValidationScenario = async () => {
    if (!activeValidationScenario) return

    setCleanupLoading(true)
    setActionError(null)
    try {
      await apiClient.post<SetupCleanupResponse>(`/api/scenarios/${activeValidationScenario.name}/cleanup`, {})
      setActiveValidationScenario(null)
      onScenarioChange?.(null)
      onCleanupComplete?.()
    } catch (error) {
      console.error('Failed to cleanup validation scenario:', error)
      setActionError('Cleanup failed — see backend logs.')
    } finally {
      setCleanupLoading(false)
    }
  }

  useEffect(() => {
    const fetchScenarios = async () => {
      try {
        const data = await apiClient.get<Scenario[]>('/api/scenarios')
        setScenarios(data)
      } catch (error) {
        console.error('Failed to fetch scenarios:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchScenarios()
  }, [])

  if (loading) {
    return <div className="text-sm text-text-secondary">Loading scenarios...</div>
  }

  // Hide component in production mode (demo mode disabled)
  if (!demoMode) {
    return null
  }

  const isSetupDisabled = !selectedScenario || activeValidationScenario?.name === selectedScenario.name || setupLoading
  const isCleanupActive = !!activeValidationScenario

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Scenario Dropdown */}
      <div className="relative">
        <div
          onClick={() => setDropdownOpen(!dropdownOpen)}
          className="flex items-center gap-2 px-3 py-1.5 border border-border rounded-lg text-sm text-text-primary hover:bg-hover-bg transition-colors bg-drawer cursor-pointer min-w-[12rem]"
        >
          <span className="font-medium">
            {selectedScenario?.displayName || activeValidationScenario?.displayName || 'Select Scenario'}
          </span>
          <svg
            className={`w-4 h-4 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {dropdownOpen && (
          <>
            <div
              className="fixed inset-0 z-50"
              onClick={() => setDropdownOpen(false)}
            />
            <div role="listbox" className="absolute right-0 top-full mt-2 w-64 bg-drawer border border-border rounded-md shadow-lg z-[1000] overflow-hidden" style={{ backgroundColor: 'var(--bg-drawer)' }}>
              <button
                key="select-option"
                onClick={() => {
                  setDropdownOpen(false)
                  if (workflowType === 'SIMULATION') {
                    stopSimulation()
                  } else {
                    setSelectedScenario(null)
                    onScenarioChange?.(null)
                  }
                }}
                className="w-full text-left px-3 py-2 text-sm text-text-secondary border-b border-border"
                style={{ backgroundColor: 'var(--bg-drawer)' }}
              >
                --select--
              </button>
              {filteredScenarios.map((scenario) => {
                const isSelected =
                  (workflowType === 'SIMULATION' && scenario.name === selectedScenario?.name) ||
                  (workflowType !== 'SIMULATION' && scenario.name === activeValidationScenario?.name)
                return (
                  <button
                    key={scenario.name}
                    role="option"
                    aria-selected={isSelected}
                    onClick={() => {
                      if (workflowType === 'SIMULATION') {
                        switchScenario(scenario.name)
                      } else {
                        setSelectedScenario(scenario)
                      }
                      setDropdownOpen(false)
                    }}
                    className={`w-full flex items-center gap-2 text-left px-3 py-2 text-sm transition-colors hover:bg-hover-bg ${
                      isSelected ? 'bg-accent-blue/10 text-accent-blue' : 'text-text-primary'
                    }`}
                    style={{ backgroundColor: isSelected ? undefined : 'var(--bg-drawer)' }}
                  >
                    {isSelected && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="shrink-0">
                        <path d="M20 6 9 17l-5-5" />
                      </svg>
                    )}
                    {scenario.displayName}
                  </button>
                )
              })}
            </div>
          </>
        )}
      </div>

      {/* Manual-provisioning workflows (VALIDATION / SWITCH_LIST): Setup/Cleanup Buttons */}
      {workflowType !== 'SIMULATION' && (
        <>
          <button
            onClick={setupValidationScenario}
            disabled={isSetupDisabled}
            className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-all ${
              isSetupDisabled
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-green-600 text-white hover:bg-green-700'
            }`}
          >
            {setupLoading ? 'Setting up...' : 'Setup'}
          </button>

          <button
            onClick={cleanupValidationScenario}
            disabled={!isCleanupActive || cleanupLoading}
            className={`px-4 py-1.5 text-sm rounded-lg font-medium transition-all ${
              isCleanupActive && !cleanupLoading
                ? 'bg-red-600 text-white hover:bg-red-700 animate-pulse'
                : !isCleanupActive
                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                : 'bg-gray-600 text-gray-400 cursor-not-allowed'
            }`}
          >
            {cleanupLoading ? 'Cleaning up...' : 'Cleanup'}
          </button>
        </>
      )}

      {actionError && (
        <span className="text-xs text-red-400 max-w-[180px]">{actionError}</span>
      )}
    </div>
  )
}

export default ScenarioControls
